import React, { useState, useEffect, useCallback } from 'react';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { useTranslation } from '../../../lib/i18n';
import { justimmoExportAPI } from '../../../lib/api';
import { toast } from 'sonner';
import {
  Building2, Link2, Loader2, CheckCircle2, Download, Home, ArrowRight,
  AlertCircle, User as UserIcon
} from 'lucide-react';
import JustimmoGuide from '../JustimmoGuide';

/**
 * Step 2: connect Justimmo + pick which agent in the agency you are.
 *
 * Multi-tenant safety lives here. The Justimmo Export API returns ALL
 * properties of the agency, not just the connected user's listings –
 * that's how the API is shaped, no per-user auth on their side. So the
 * onboarding flow has three phases:
 *
 *   1. Enter API username + password → connect
 *   2. **Pick yourself from the team-member dropdown**. We persist the
 *      choice as `default_owner_id` on the connection. From then on, the
 *      list-properties endpoint auto-scopes, and the import endpoint
 *      refuses to ingest someone else's listing if the IDs don't match.
 *   3. Browse + import your filtered property list.
 *
 * Without phase 2, an Austrian Makler at a multi-agent Büro could
 * auto-post a colleague's listing under their own name. Skipping the
 * dropdown is intentionally NOT an option. The property list stays
 * empty until they pick.
 */
export default function ImportStep({ onImport, onConnect, importedCount, isConnected }) {
  const { t } = useTranslation();
  const [apiUsername, setApiUsername] = useState('');
  const [apiPassword, setApiPassword] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importingAll, setImportingAll] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [localImported, setLocalImported] = useState(importedCount);

  // Agent picker state. `agentId === ''` means "not yet picked"; once
  // saved on the connection, `loadProperties` becomes safe to call.
  const [teamMembers, setTeamMembers] = useState([]);
  const [agentId, setAgentId] = useState('');
  const [savingAgent, setSavingAgent] = useState(false);
  const [teamLoading, setTeamLoading] = useState(false);

  // Pull the current connection state (incl. saved default_owner_id) so
  // a returning user lands on the property list directly without
  // re-picking. Returns true if an agent is already selected.
  const refreshConnectionAgent = useCallback(async () => {
    try {
      const res = await justimmoExportAPI.getStatus();
      const id = res.data?.default_owner_id;
      if (id) setAgentId(String(id));
      return Boolean(id);
    } catch (_) {
      return false;
    }
  }, []);

  const loadTeam = useCallback(async () => {
    setTeamLoading(true);
    try {
      const res = await justimmoExportAPI.getTeam();
      setTeamMembers(res.data?.team_members || []);
    } catch (_) {
      // Non-fatal; the user can still connect, but the picker shows empty.
      // We surface a banner below explaining what to do if this happens.
    } finally {
      setTeamLoading(false);
    }
  }, []);

  // Property list is filtered server-side using the connection's saved
  // `default_owner_id`, so we don't pass owner_id from the client. That
  // way a frontend bug can't accidentally widen the scope.
  const loadProperties = useCallback(async () => {
    setLoading(true);
    try {
      const res = await justimmoExportAPI.listProperties({
        limit: 20,
        offset: 0,
        only_active: true,
      });
      setProperties(res.data.properties || []);
      setTotalCount(res.data.total_count || 0);
    } catch (err) {
      toast.error(t('onboarding.step2.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // On mount: if already connected from a prior session, jump straight to
  // (a) loading the team for the picker AND (b) checking whether the
  // picker has already been completed. If yes → straight to property list.
  useEffect(() => {
    (async () => {
      try {
        const res = await justimmoExportAPI.getStatus();
        if (res.data?.connected) {
          onConnect(true);
          loadTeam();
          const alreadyPicked = await refreshConnectionAgent();
          if (alreadyPicked) loadProperties();
        }
      } catch (_) {
        // Not connected yet. Nothing to do.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async (e) => {
    e.preventDefault();
    if (!apiUsername || !apiPassword) {
      toast.error(t('onboarding.step2.enterCredentials'));
      return;
    }
    setConnecting(true);
    try {
      const res = await justimmoExportAPI.connect({
        api_username: apiUsername,
        api_password: apiPassword,
        auto_import_new: true,
      });
      toast.success(t('onboarding.step2.propertiesFound', { count: res.data.total_properties }));
      onConnect(true);
      setApiUsername('');
      setApiPassword('');
      // Connect → load team list (so the dropdown can render). Property
      // list waits until the user picks their agent.
      loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('onboarding.step2.connectionFailed'));
    } finally {
      setConnecting(false);
    }
  };

  const handlePickAgent = async (e) => {
    const value = e.target.value;
    setAgentId(value);
    if (!value) return;
    setSavingAgent(true);
    try {
      const res = await justimmoExportAPI.updateConnection({
        default_owner_id: value,
      });
      const backfilled = res.data?.backfilled_properties || 0;
      if (backfilled > 0) {
        toast.success(
          t('onboarding.step2.agentBackfilled', { count: backfilled })
            || `${backfilled} bestehende Immobilien deinem Profil zugeordnet.`
        );
      } else {
        toast.success(
          t('onboarding.step2.agentPicked')
            || 'Profil gespeichert. Wir zeigen dir nur deine eigenen Listings.'
        );
      }
      // Reload properties. Server now auto-filters via the saved owner_id.
      loadProperties();
    } catch (err) {
      toast.error(
        err.response?.data?.detail
          || t('onboarding.step2.agentPickFailed')
          || 'Konnte dein Profil nicht speichern.'
      );
      setAgentId('');
    } finally {
      setSavingAgent(false);
    }
  };

  const handleImportSingle = async (prop) => {
    setImportingId(prop.justimmo_id);
    try {
      const res = await justimmoExportAPI.importProperty(prop.justimmo_id);
      if (res.data.already_exists) {
        toast.info(t('onboarding.step2.alreadyImported'));
      } else {
        const newCount = localImported + 1;
        setLocalImported(newCount);
        onImport(newCount);
        toast.success(t('onboarding.step2.importedSingle', { title: res.data.title }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('onboarding.step2.importFailed'));
    } finally {
      setImportingId(null);
    }
  };

  const handleImportAll = async () => {
    setImportingAll(true);
    let count = 0;
    for (const prop of properties) {
      try {
        const res = await justimmoExportAPI.importProperty(prop.justimmo_id);
        if (!res.data.already_exists) count++;
      } catch (err) {
        // Skip failed imports
      }
    }
    const newCount = localImported + count;
    setLocalImported(newCount);
    onImport(newCount);
    toast.success(t('onboarding.step2.imported', { count }));
    setImportingAll(false);
  };

  // The agent picker UI block. Shown once connected, before the
  // property list. Stays visible as a "switch profile" affordance even
  // after the user picks (in case they accidentally picked the wrong
  // person and need to fix it).
  const agentPicker = (
    <div className="p-4 rounded-xl border-2 border-[#1f4d3a]/30 bg-[#f0f3ec] space-y-2">
      <div className="flex items-center gap-2">
        <UserIcon className="w-4 h-4 text-[#1f4d3a]" />
        <p className="text-sm font-medium text-[#23211a]">
          {t('onboarding.step2.agentPickerTitle') || 'Welcher Makler bist du?'}
        </p>
      </div>
      <p className="text-xs text-[#6f6a5c] leading-relaxed">
        {t('onboarding.step2.agentPickerHelp') ||
          'Justimmo gibt uns alle Immobilien deiner Agentur. Wähle dich hier aus, damit wir nur deine Listings importieren. Niemals die deiner Kollegen.'}
      </p>
      {teamLoading ? (
        <div className="flex items-center gap-2 text-xs text-[#6f6a5c] py-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          {t('common.loading') || 'Wird geladen…'}
        </div>
      ) : teamMembers.length === 0 ? (
        <div className="flex items-start gap-2 text-xs text-[#9a6d1f] bg-[#f6edd8] border border-[#c7a869] rounded-md p-2">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            {t('onboarding.step2.teamEmpty') ||
              'Wir konnten die Mitarbeiterliste nicht laden. Stelle sicher, dass dein Justimmo-API-Account die "team/list"-Berechtigung hat, oder schreib uns kurz.'}
          </span>
        </div>
      ) : (
        <div className="relative">
          <select
            value={agentId}
            onChange={handlePickAgent}
            disabled={savingAgent}
            className="w-full text-sm bg-[#fffdf7] border border-[#d8d0bd] rounded-md px-3 py-2 text-[#23211a] focus:outline-none focus:ring-2 focus:ring-[#1f4d3a]/40"
          >
            <option value="">
              {t('onboarding.step2.agentPickerPlaceholder') || 'Bitte auswählen…'}
            </option>
            {teamMembers.map((m) => {
              const fullName = [m.first_name, m.last_name].filter(Boolean).join(' ').trim();
              return (
                <option key={m.id} value={m.id}>
                  {fullName || `Mitarbeiter #${m.id}`}
                  {m.position ? ` · ${m.position}` : ''}
                </option>
              );
            })}
          </select>
          {savingAgent && (
            <Loader2 className="w-4 h-4 animate-spin text-[#6f6a5c] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex p-4 bg-[#f0e6cf] rounded-2xl mb-4">
          <Building2 className="w-10 h-10 text-[#8f6f3a]" />
        </div>
        <h2 className="text-2xl font-bold text-[#23211a] mb-2">
          {t('onboarding.step2.title')}
        </h2>
        <p className="text-[#6f6a5c]">
          {t('onboarding.step2.subtitle')}
        </p>
      </div>

      {/* Imported count badge */}
      {localImported > 0 && (
        <div className="flex items-center justify-center gap-2 p-3 bg-[#e4f0e8] border border-[#1e7a4f]/40 rounded-lg">
          <CheckCircle2 className="w-5 h-5 text-[#1e7a4f]" />
          <span className="text-sm text-[#1e7a4f] font-medium">
            {t('onboarding.step2.imported', { count: localImported })}
          </span>
        </div>
      )}

      {!isConnected ? (
        /* Connection Form */
        <div className="space-y-4">
          {/* Replaces the previous 5-line text-only instruction list with a
              richer 8-step illustrated walkthrough using the screenshots
              under public/justimmo-guide/. Stays collapsed by default so
              users who already know the flow aren't blocked by it. */}
          <JustimmoGuide />

          <form onSubmit={handleConnect} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm text-[#4a463b] mb-1 block">{t('onboarding.step2.username')}</label>
                <Input
                  type="text"
                  value={apiUsername}
                  onChange={(e) => setApiUsername(e.target.value)}
                  placeholder={t('onboarding.step2.username')}
                  className="bg-[#fffdf7] border-[#d8d0bd]"
                  data-testid="onboarding-justimmo-user"
                />
              </div>
              <div>
                <label className="text-sm text-[#4a463b] mb-1 block">{t('onboarding.step2.password')}</label>
                <Input
                  type="password"
                  value={apiPassword}
                  onChange={(e) => setApiPassword(e.target.value)}
                  placeholder={t('onboarding.step2.password')}
                  className="bg-[#fffdf7] border-[#d8d0bd]"
                  data-testid="onboarding-justimmo-pass"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={connecting}
              className="w-full bg-[#1f4d3a] hover:bg-[#1f4d3a]"
            >
              {connecting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('common.loading')}</>
              ) : (
                <><Link2 className="w-4 h-4 mr-2" /> {t('onboarding.step2.connectButton')}</>
              )}
            </Button>
          </form>

          <p className="text-center text-xs text-[#8a8474]">
            {t('onboarding.step2.skipManual')}
          </p>
        </div>
      ) : (
        /* Properties List */
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#1e7a4f]" />
              <span className="text-sm font-medium text-[#1e7a4f]">
                {t('onboarding.step2.connected')}
              </span>
            </div>
            {agentId && properties.length > 0 && (
              <Button
                size="sm"
                onClick={handleImportAll}
                disabled={importingAll}
                className="bg-[#1f4d3a] hover:bg-[#1f4d3a] text-xs"
              >
                {importingAll ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> {t('onboarding.step2.importing')}</>
                ) : (
                  <><Download className="w-3.5 h-3.5 mr-1" /> {t('onboarding.step2.importAll')} ({totalCount})</>
                )}
              </Button>
            )}
          </div>

          {/* Agent picker. Surfaces above the property list. Mandatory:
              property list stays empty until an agent is picked. */}
          {agentPicker}

          {!agentId ? (
            <div className="text-center py-8">
              <UserIcon className="w-12 h-12 text-[#9c9585] mx-auto mb-3" />
              <p className="text-sm text-[#6f6a5c]">
                {t('onboarding.step2.pickAgentFirst') ||
                  'Wähle dich oben aus der Mitarbeiterliste, um deine Immobilien zu sehen.'}
              </p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-[#1f4d3a] animate-spin" />
            </div>
          ) : properties.length === 0 ? (
            <div className="text-center py-8">
              <Home className="w-12 h-12 text-[#9c9585] mx-auto mb-3" />
              <p className="text-[#6f6a5c]">{t('onboarding.step2.noProperties')}</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {properties.map((prop) => (
                <div
                  key={prop.justimmo_id}
                  className="flex items-center gap-3 p-3 bg-[#f2ecdf] border border-[#d8d0bd] rounded-lg"
                >
                  <div className="w-14 h-14 flex-shrink-0 rounded-md overflow-hidden bg-[#d8d0bd]">
                    {prop.images?.[0] ? (
                      <img src={prop.images[0]} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Home className="w-6 h-6 text-[#8a8474]" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#23211a] truncate">
                      {prop.title || `#${prop.objektnummer}`}
                    </p>
                    <p className="text-xs text-[#8a8474]">{prop.city}, {prop.zip_code}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleImportSingle(prop)}
                    disabled={importingId === prop.justimmo_id}
                    className="border-[#1f4d3a]/30 text-[#1f4d3a] hover:bg-[#e9efe7] text-xs"
                  >
                    {importingId === prop.justimmo_id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <><Download className="w-3.5 h-3.5 mr-1" /> Import</>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
